'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["trackId", "lessonId", "contentId", "language"];
import { Component } from 'react';
import PortalIdParser from 'PortalIdParser';
import UsageTracker from '../../../utils/UsageTracker';
import { TrackingContentTypes } from '../../../constants/Tracking';
import NotStartedPracticalExercise from './NotStartedPracticalExercise';
import InProgressPracticalExercise from './InProgressPracticalExercise';
import PracticalExerciseRequiredProduct from './PracticalExerciseRequiredProduct';
import PracticalExerciseRequiredAccess from './PracticalExerciseRequiredAccess';
import PracticalExerciseLoading from './PracticalExerciseLoading';
import PracticalExerciseError from './PracticalExerciseError';
import { ContentProgressTypes, PracticalExerciseTypes } from '../../../constants/ContentConstants';
import PracticalExerciseWithSubmission from './PracticalExerciseWithSubmission';
import InReviewPracticalExerciseCondensed from './InReviewPracticalExerciseCondensed';
import CompletedPracticalExerciseCondensed from './CompletedPracticalExerciseCondensed';
import FailedPracticalExerciseCondensed from './FailedPracticalExerciseCondensed';
import { PRACTICAL_EXERCISE_URL_FRAGMENT } from '../../../constants/Navigation';
import { useContentStatus } from '../../userProgress';
import { useDeletePracticalExerciseProgress, usePracticalExercise, usePracticalExerciseRequirements, useSetManuallyGradedPracticalExerciseSubmitted, useSetManuallyGradedPracticalExerciseWithAssetSubmitted, useSetPracticalExerciseStarted } from '..';
import { jsx as _jsx } from "react/jsx-runtime";
class PracticalExercise extends Component {
  constructor(...args) {
    super(...args);
    this.track = action => {
      const {
        practicalExercise,
        practicalExerciseRequirements,
        contentId,
        trackId,
        trackName,
        lessonId,
        lessonName
      } = this.props;
      const graded = practicalExercise.graded;
      const manuallyApproved = practicalExercise.manuallyApproved;
      const name = practicalExercise.name;
      const trackerNotRequired = practicalExercise.trackerNotRequired;
      const requiredProduct = practicalExerciseRequirements && practicalExerciseRequirements.requiredProducts && practicalExerciseRequirements.requiredProducts[0];
      const trackingMethod = trackerNotRequired ? UsageTracker.track : UsageTracker.trackExternalLink;
      trackingMethod.call(UsageTracker, 'contentInteraction', {
        action,
        contentId,
        contentLanguage: practicalExercise.language,
        contentType: TrackingContentTypes.PRACTICAL_EXERCISE_FLOW,
        trackId,
        trackName,
        lessonId,
        lessonName,
        contentName: name,
        productName: requiredProduct || '',
        isUntrackedExercise: !!trackerNotRequired,
        graded: !!graded,
        manuallyApproved: !!manuallyApproved
      });
    };
    this.handleClickThroughToProductPage = () => {
      this.track('click-to-product-page');
    };
    this.handleSkipScreenClickSkip = () => {
      const {
        onFinish
      } = this.props;
      this.track('click-skip-screen-skip');
      onFinish();
    };
    this.handleSkipScreenClickRetry = () => {
      this.track('click-skip-screen-retry');
    };
    this.handleStartExercise = () => {
      const {
        practicalExercise,
        onStartExercise,
        trackId,
        lessonId,
        contentId
      } = this.props;
      const isExternal = practicalExercise.trackerNotRequired;
      this.track('click-start-exercise');
      if (isExternal) {
        onStartExercise(trackId && Number(trackId), Number(lessonId), Number(contentId));
      }
    };
    this.handleReviewExercise = () => {
      this.track('click-review-exercise');
      const {
        practicalExercise,
        onExerciseFinished,
        contentId,
        trackId,
        lessonId
      } = this.props;
      const language = practicalExercise.language;
      onExerciseFinished(Number(contentId), language, Number(trackId) || undefined, Number(lessonId) || undefined);
    };
    this.handleSubmitManualExercise = (assetType, reference) => {
      this.track('click-review-exercise');
      const {
        onSubmitWithAsset,
        contentId
      } = this.props;
      onSubmitWithAsset(Number(contentId), assetType, reference);
    };
    this.handleClickResubmit = () => {
      this.track('retry-practical-exercise');
      this.props.onRetryPracticalExercise(Number(this.props.contentId));
    };
  }
  render() {
    const {
      onFinish,
      contentId,
      practicalExerciseStatus,
      practicalExerciseStatusError,
      practicalExerciseStatusLoading,
      exerciseLocation,
      lessonId,
      trackId,
      practicalExercise,
      practicalExerciseLoading,
      practicalExerciseError,
      practicalExerciseRequirements,
      practicalExerciseRequirementsLoading,
      practicalExerciseRequirementsError
    } = this.props;
    const requiredProduct = practicalExerciseRequirements && practicalExerciseRequirements.requiredProducts && practicalExerciseRequirements.requiredProducts[0];
    const requiredAccess = practicalExerciseRequirements && practicalExerciseRequirements.requiredAccess && practicalExerciseRequirements.requiredAccess[0];
    const isManual = practicalExercise && practicalExercise.manuallyApproved;
    const error = practicalExerciseError || practicalExerciseRequirementsError || practicalExerciseStatusError;
    const loading = practicalExerciseLoading || practicalExerciseRequirementsLoading || practicalExerciseStatusLoading;
    const practicalExerciseType = practicalExercise && practicalExercise.type;
    if (loading) {
      return /*#__PURE__*/_jsx(PracticalExerciseLoading, {});
    }
    if (error) {
      return /*#__PURE__*/_jsx(PracticalExerciseError, {
        error: error
      });
    }
    if (practicalExerciseStatus === ContentProgressTypes.COMPLETED) {
      return /*#__PURE__*/_jsx(CompletedPracticalExerciseCondensed, {
        advanceToNextContent: onFinish
      });
    }
    if (practicalExerciseStatus === ContentProgressTypes.IN_REVIEW) {
      return /*#__PURE__*/_jsx(InReviewPracticalExerciseCondensed, {
        advanceToNextContent: onFinish,
        contentId: contentId,
        isManual: isManual,
        isPassRequiredForCert: practicalExercise.graded,
        lessonId: lessonId,
        onResubmitClick: this.handleClickResubmit,
        resubmitType: 'resubmit',
        title: practicalExercise.name,
        trackId: trackId
      });
    }
    if (practicalExerciseStatus === ContentProgressTypes.STARTED && practicalExerciseType !== PracticalExerciseTypes.MANUALLY_GRADED_WITH_FILE_SUBMISSION) {
      return /*#__PURE__*/_jsx(InProgressPracticalExercise, {
        advanceToNextContent: onFinish,
        contentId: contentId,
        exercise: practicalExercise,
        exerciseLocation: exerciseLocation,
        lessonId: lessonId,
        onReviewExercise: this.handleReviewExercise,
        trackId: trackId
      });
    }
    if (practicalExerciseStatus === ContentProgressTypes.FAILED) {
      return /*#__PURE__*/_jsx(FailedPracticalExerciseCondensed, {
        advanceToNextContent: onFinish,
        contentId: contentId,
        isPassRequiredForCert: practicalExercise.graded,
        lessonId: lessonId,
        onResubmitClick: this.handleClickResubmit,
        resubmitType: 'resubmit',
        title: practicalExercise.name,
        trackId: trackId
      });
    }
    if (requiredProduct) {
      return /*#__PURE__*/_jsx(PracticalExerciseRequiredProduct, {
        advanceToNextContent: onFinish,
        onClickProductLink: this.handleClickThroughToProductPage,
        portalId: PortalIdParser.getPortalIdFromPath(),
        requiredProduct: requiredProduct
      });
    }
    if (requiredAccess) {
      return /*#__PURE__*/_jsx(PracticalExerciseRequiredAccess, {
        advanceToNextContent: onFinish,
        product: requiredAccess
      });
    }
    if (practicalExerciseType === PracticalExerciseTypes.MANUALLY_GRADED_WITH_FILE_SUBMISSION) {
      return /*#__PURE__*/_jsx(PracticalExerciseWithSubmission, {
        advanceToNextContent: onFinish,
        isPassRequiredForCert: practicalExercise.graded,
        isSubmitting: false,
        onSubmit: this.handleSubmitManualExercise,
        title: practicalExercise.name
      });
    }
    return /*#__PURE__*/_jsx(NotStartedPracticalExercise, {
      advanceToNextContent: onFinish,
      exercise: practicalExercise,
      exerciseLocation: exerciseLocation,
      onStartClick: this.handleStartExercise
    });
  }
}
const PracticalExerciseFunctionalContainer = _ref => {
  let {
      trackId,
      lessonId,
      contentId,
      language
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    practicalExercise,
    practicalExerciseLoading,
    practicalExerciseError
  } = usePracticalExercise(Number(lessonId), Number(contentId), language);
  const {
    practicalExerciseRequirements,
    practicalExerciseRequirementsLoading,
    practicalExerciseRequirementsError
  } = usePracticalExerciseRequirements(Number(contentId));
  const {
    contentStatus,
    contentStatusLoading,
    contentStatusError
  } = useContentStatus(Number(contentId), language);
  const [startPracticalExercise] = useSetPracticalExerciseStarted(language);
  const [submitPracticalExerciseWithAsset] = useSetManuallyGradedPracticalExerciseWithAssetSubmitted(language);
  const [submitManuallyGradedPracticalExercise] = useSetManuallyGradedPracticalExerciseSubmitted(language);
  const [deletePracticalExerciseProgress] = useDeletePracticalExerciseProgress();
  const searchParams = new URLSearchParams(window.location.search);
  if (!searchParams.get('language')) {
    searchParams.set('language', language);
  }
  const exerciseLocation = `${PRACTICAL_EXERCISE_URL_FRAGMENT}/${contentId}/${lessonId}/${trackId || ''}?${searchParams.toString()}`;
  return /*#__PURE__*/_jsx(PracticalExercise, Object.assign({
    contentId: contentId,
    exerciseLocation: exerciseLocation,
    lessonId: lessonId,
    onExerciseFinished: submitManuallyGradedPracticalExercise,
    onRetryPracticalExercise: deletePracticalExerciseProgress,
    onStartExercise: startPracticalExercise,
    onSubmitWithAsset: submitPracticalExerciseWithAsset,
    practicalExercise: practicalExercise,
    practicalExerciseError: practicalExerciseError,
    practicalExerciseLoading: practicalExerciseLoading,
    practicalExerciseRequirements: practicalExerciseRequirements,
    practicalExerciseRequirementsError: practicalExerciseRequirementsError,
    practicalExerciseRequirementsLoading: practicalExerciseRequirementsLoading,
    practicalExerciseStatus: contentStatus,
    practicalExerciseStatusError: contentStatusError,
    practicalExerciseStatusLoading: contentStatusLoading,
    trackId: trackId
  }, props));
};
export const WEBPACK_3_FORCE_MODULE_IMPORT = 1;
export default PracticalExerciseFunctionalContainer;